/**
 * Quality Gaps Card
 *
 * Displays identified gaps and data quality issues from the extraction
 * pipeline. Grouped by severity with clear visual indicators.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { AlertTriangle, AlertOctagon, Info } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import type { RichExtractionQuality } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

const SEVERITY_CONFIG: Record<string, { icon: typeof AlertTriangle; color: string; bg: string }> = {
  critical: { icon: AlertOctagon, color: '#DC2626', bg: '#FEE2E2' },
  moderate: { icon: AlertTriangle, color: '#D97706', bg: '#FEF3C7' },
  minor: { icon: Info, color: '#6B7280', bg: '#F3F4F6' },
};

interface QualityGapsCardProps {
  quality: RichExtractionQuality;
}

export function QualityGapsCard({ quality }: QualityGapsCardProps) {
  const colors = useThemeColors();
  const { gapsIdentified, sectionConfidences } = quality;

  // Sort: critical first, then moderate, then minor
  const sortedGaps = [...gapsIdentified].sort((a, b) => {
    const order = ['critical', 'moderate', 'minor'];
    return order.indexOf(a.severity) - order.indexOf(b.severity);
  });

  const criticalCount = gapsIdentified.filter(g => g.severity === 'critical').length;
  const moderateCount = gapsIdentified.filter(g => g.severity === 'moderate').length;
  const minorCount = gapsIdentified.filter(g => g.severity === 'minor').length;

  return (
    <Animated.View entering={FadeIn.delay(300).duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center gap-2.5 mb-2">
            <View
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: '#FEF3C7' }}
            >
              <AlertTriangle size={16} color="#D97706" />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                Quality Analysis
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {gapsIdentified.length} issue{gapsIdentified.length !== 1 ? 's' : ''} detected
              </Text>
            </View>
          </View>

          {/* Severity summary pills */}
          <View className="flex-row gap-3">
            {criticalCount > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#DC2626' }} />
                <Text style={{ color: '#DC2626' }} className="text-xs font-semibold">{criticalCount}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">critical</Text>
              </View>
            )}
            {moderateCount > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D97706' }} />
                <Text style={{ color: '#D97706' }} className="text-xs font-semibold">{moderateCount}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">moderate</Text>
              </View>
            )}
            {minorCount > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#6B7280' }} />
                <Text style={{ color: '#6B7280' }} className="text-xs font-semibold">{minorCount}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">minor</Text>
              </View>
            )}
          </View>
        </View>

        {/* Section confidence bars */}
        {sectionConfidences.length > 0 && (
          <View className="px-4 pb-3" style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mt-3 mb-2">
              Section Confidence
            </Text>
            {sectionConfidences.map((sc, i) => {
              const barColor = sc.confidence >= 70 ? colors.success
                : sc.confidence >= 40 ? colors.warning
                : colors.error;
              return (
                <View key={sc.section} className="flex-row items-center gap-2 mb-2">
                  <Text style={{ color: colors.text.secondary }} className="text-xs w-20 capitalize">
                    {sc.section}
                  </Text>
                  <View className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.tertiary }}>
                    <View
                      className="h-full rounded-full"
                      style={{ width: `${sc.confidence}%`, backgroundColor: barColor }}
                    />
                  </View>
                  <Text style={{ color: barColor }} className="text-xs font-bold w-8 text-right">
                    {sc.confidence}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Gap list */}
        {sortedGaps.length > 0 && (
          <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
            {sortedGaps.map((gap, i) => {
              const config = SEVERITY_CONFIG[gap.severity] || SEVERITY_CONFIG.minor;
              const GapIcon = config.icon;

              return (
                <Animated.View
                  key={`${gap.section}-${i}`}
                  entering={FadeInDown.delay(i * 30).duration(250)}
                  className="flex-row items-start gap-3 px-4 py-2.5"
                  style={i < sortedGaps.length - 1 ? { borderBottomWidth: 0.5, borderBottomColor: colors.border.light } : undefined}
                >
                  <View
                    className="w-6 h-6 rounded-md items-center justify-center mt-0.5"
                    style={{ backgroundColor: config.bg }}
                  >
                    <GapIcon size={12} color={config.color} />
                  </View>
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2 mb-0.5">
                      <View className="px-1.5 py-0.5 rounded" style={{ backgroundColor: config.bg }}>
                        <Text style={{ color: config.color }} className="text-xs font-bold">
                          {gap.severity}
                        </Text>
                      </View>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs capitalize">
                        {gap.section}
                      </Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-xs leading-4">
                      {gap.description}
                    </Text>
                  </View>
                </Animated.View>
              );
            })}
          </View>
        )}
      </View>
    </Animated.View>
  );
}
